import { useEffect, useMemo } from 'react';
import type { DecryptedConversation } from '../../hooks/useConversations';
import { useTheme } from '../../hooks/useTheme';
import { usePlatformCapabilities } from '../../config';
import type { SpaceChannelUnreadState } from '../../services/spaceSocketHandlers';

/**
 * Computes conversation + Spaces unread totals and syncs the desktop app badge.
 *
 * Channels whose notification level hides counts still carry a mention flag;
 * each such channel adds 1 so the mention is not lost from the totals.
 */
export function useConversationSidebarBadge(
  conversations: DecryptedConversation[],
  unreadBySpace: Record<string, number>,
  unreadByChannel: Record<string, SpaceChannelUnreadState> = {},
): { totalUnread: number; totalSpacesUnread: number } {
  const totalUnread = conversations.reduce(
    (sum, c) => sum + (c.hasUnread ? 1 : 0) + c.unreadCount,
    0,
  );

  const totalSpacesUnread = useMemo(() => {
    let total = Object.values(unreadBySpace).reduce((sum, n) => sum + n, 0);
    for (const state of Object.values(unreadByChannel)) {
      if (state.mention && state.unread === 0) total += 1;
    }
    return total;
  }, [unreadBySpace, unreadByChannel]);

  const { appWindow } = usePlatformCapabilities();
  const { activeTheme } = useTheme();
  const accentHex = activeTheme?.colors.accentPrimary;
  const secondaryHex = activeTheme?.colors.accentSecondary;

  useEffect(() => {
    appWindow?.setBadgeCount(totalUnread + totalSpacesUnread, accentHex, secondaryHex);
  }, [totalUnread, totalSpacesUnread, appWindow, accentHex, secondaryHex]);

  return { totalUnread, totalSpacesUnread };
}
